import { useMutation, useQuery, useQueryClient } from "react-query";
import useFeedback from "../components/feedback/useFeedback";
import { ESOCIAL_JT_SERVICE_URL } from "../shared/env";
import createApi, { createQueryFetcher } from "./api";

export const api = createApi(ESOCIAL_JT_SERVICE_URL);
const queryFetcher = createQueryFetcher(api);
const REFRESH_INTERVAL = 5000;
const PAGE_SIZE = 25;
const GATEWAY_API = "/api/v1";

export function useOcorrencias() {
  return useQuery(`/ocorrencias/dados-basicos`, queryFetcher, {
    refetchInterval: REFRESH_INTERVAL
  });
}

export function useOcorrenciasPaginado(
  page = 0,
  estados = [],
  expressao = "",
  tipo = "",
  incluirArquivados = false,
  cpf = "",
  periodoApuracao = "",
  dataInicio = "",
  dataFim = ""
) {
  const query = `/ocorrencias/paginado?page=${page}&size=${PAGE_SIZE}&estados=${estados.join()}&expressao=${expressao}&tipos=${tipo}&incluirArquivados=${incluirArquivados}&cpf=${cpf}&periodoApuracao=${periodoApuracao}&dataInicio=${dataInicio}&dataFim=${dataFim}`;
  return useQuery(query, queryFetcher, {
    refetchInterval: REFRESH_INTERVAL,
    keepPreviousData: true
  });
}

export function useDetalheOcorrencia(id) {
  return useQuery(`/ocorrencias/${id}`, queryFetcher, {
    refetchInterval: REFRESH_INTERVAL
  });
}

export function useEnviosGatewayPaginado(
  page = 0,
  estados = [],
  expressao = "",
  tipo = "",
  dataInicio = "",
  dataFim = ""
) {
  const params = new URLSearchParams({
    page,
    size: PAGE_SIZE,
    estados: estados.join(),
    expressao,
    tipo,
    dataInicio,
    dataFim
  });
  const query = `${GATEWAY_API}/envios?${params.toString()}`;
  return useQuery(query, queryFetcher, {
    refetchInterval: REFRESH_INTERVAL,
    keepPreviousData: true
  });
}

export function useDetalheEnvioGateway(id) {
  return useQuery(`${GATEWAY_API}/envios/${id}`, queryFetcher, {
    refetchInterval: REFRESH_INTERVAL,
    enabled: !!id
  });
}

export function useXmlEnviadoGateway(id) {
  return useQuery(`${GATEWAY_API}/envios/${id}/xml`, queryFetcher, {
    enabled: !!id,
    staleTime: Infinity
  });
}

export function useTiposEnviosGateway() {
  return useQuery(`${GATEWAY_API}/envios/tipos`, queryFetcher);
}

export function useTiposEnviados() {
  return useQuery(`/ocorrencias/tipos`, queryFetcher);
}

export function useLimparProducaoRestrita() {
  const queryClient = useQueryClient();
  return useMutation(() => api.post(`/producao-restrita/acoes/limpar`), {
    onSuccess: () =>
      queryClient.setQueryData(
        `/ocorrencias/paginado?page=0&size=${PAGE_SIZE}`,
        { empty: true }
      )
  });
}

export function useESocialJTStatus() {
  return useQuery(`/actuator/health`, queryFetcher);
}

export function useESocialGovStatus() {
  return useQuery(`/actuator/esocialhealth`, queryFetcher);
}

export function useTransmissores() {
  return useQuery(`${GATEWAY_API}/transmissores`, queryFetcher, {
    refetchInterval: REFRESH_INTERVAL
  });
}

export function useDetalheTransmissor(id) {
  return useQuery(`${GATEWAY_API}/transmissores/${id}`, queryFetcher, {
    enabled: !!id
  });
}

export function useArquivar() {
  const queryClient = useQueryClient();
  const { showSuccess } = useFeedback();
  return useMutation((id) => api.post(`/ocorrencias/${id}/acoes/arquivar`), {
    onSuccess: () =>
      queryClient
        .invalidateQueries()
        .then(() => showSuccess(`Evento arquivado`))
  });
}

export function useDesarquivar() {
  const queryClient = useQueryClient();
  const { showInfo } = useFeedback();
  return useMutation((id) => api.post(`/ocorrencias/${id}/acoes/desarquivar`), {
    onSuccess: () =>
      queryClient
        .invalidateQueries()
        .then(() => showInfo(`Evento desarquivado`))
  });
}

export function useEnviarExclusao() {
  return useMutation((id) => api.post(`/ocorrencias/${id}/acoes/excluir`));
}

export function useBuscarIds() {
  return useMutation((filtros) => api.post(`/ocorrencias/busca-ids`, filtros));
}

export function useExcluirLista() {
  const queryClient = useQueryClient();
  return useMutation(
    (ids) => api.post(`/ocorrencias/acoes/excluir-lista`, ids),
    {
      onSuccess: () => queryClient.invalidateQueries()
    }
  );
}

export function useArquivarErros() {
  const queryClient = useQueryClient();
  const { showSuccess } = useFeedback();
  return useMutation(() => api.post(`/ocorrencias/acoes/arquivar-erros`), {
    onSuccess: (response) =>
      queryClient
        .invalidateQueries()
        .then(() => showSuccess(`${response.data} evento(s) arquivado(s)`))
  });
}

export function useArquivarExcluidosRetificados() {
  const queryClient = useQueryClient();
  const { showSuccess } = useFeedback();
  return useMutation(
    () => api.post(`/ocorrencias/acoes/arquivar-excluidos-retificados`),
    {
      onSuccess: (response) =>
        queryClient
          .invalidateQueries()
          .then(() => showSuccess(`${response.data} evento(s) arquivado(s)`))
    }
  );
}

export function useCriarOcorrencia() {
  const queryClient = useQueryClient();
  const { showSuccess } = useFeedback();
  return useMutation((json) => api.post(`/ocorrencias`, json), {
    onSuccess: () =>
      queryClient
        .invalidateQueries()
        .then(() => showSuccess("Ocorrência criada com sucesso"))
  });
}

export function useCriarTransmissor() {
  const queryClient = useQueryClient();
  return useMutation(
    (formData) => api.post(`${GATEWAY_API}/transmissores`, formData),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(`${GATEWAY_API}/transmissores`)
    }
  );
}

export function useRotacionarTransmissor() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, formData }) =>
      api.post(`${GATEWAY_API}/transmissores/${id}/certificados`, formData),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(`${GATEWAY_API}/transmissores`)
    }
  );
}

export function useExemploOcorrencia(tipo) {
  return useQuery(`/ocorrencias/exemplos/${tipo}`, queryFetcher, {
    enabled: !!tipo
  });
}
